/**
 * Read a colour the way people write one: a hex code, a CSS function, a keyword, or a bare
 * triple of numbers. Returns null for anything it cannot read — never a guess.
 *
 * Out-of-range channels are clamped, as CSS does at computed-value time, so `rgb(300 0 0)`
 * is red. Hue accepts `deg`, `grad`, `rad` and `turn`. `hsv()` and `cmyk()` are accepted
 * although CSS has neither, because design and print tools write them.
 */

import { hexForName } from "@/lib/tool-kit/color-charts/color-names";
import {
  clamp,
  cmykToRgb,
  hslToRgb,
  hsvToRgb,
  hwbToRgb,
  type Rgb,
} from "@/lib/tool-kit/color-charts/color-space";

const HEX = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/;
const FUNCTION = /^([a-z]+)\(\s*([^)]*?)\s*\)$/;
const NUMBER =
  /^([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)(%|deg|grad|rad|turn)?$/;
const SPLIT_ARGS = /\s*,\s*|\s+/;
const SLASH = /\s*\/\s*/;

interface Token {
  value: number;
  unit: string;
}

function token(text: string): Token | null {
  if (text === "none") {
    return { value: 0, unit: "" };
  }
  const match = NUMBER.exec(text);
  if (!match) {
    return null;
  }
  return { value: Number(match[1]), unit: match[2] ?? "" };
}

/** A channel that is 0–`full` as a number or 0–100 as a percentage. */
function scaled(tok: Token, full: number): number | null {
  if (tok.unit === "") {
    return tok.value;
  }
  return tok.unit === "%" ? (tok.value / 100) * full : null;
}

/** A percentage channel; a bare number is read as already being a percentage. */
function percent(tok: Token): number | null {
  return tok.unit === "" || tok.unit === "%" ? tok.value : null;
}

const HUE_UNITS: Record<string, number> = {
  "": 1,
  deg: 1,
  grad: 0.9,
  rad: 180 / Math.PI,
  turn: 360,
};

function hue(tok: Token): number | null {
  const factor = HUE_UNITS[tok.unit];
  return factor === undefined ? null : tok.value * factor;
}

function alphaOf(tok: Token | undefined): number | null {
  if (!tok) {
    return 1;
  }
  const value = scaled(tok, 1);
  return value === null ? null : clamp(value, 0, 1);
}

function parseHex(text: string): Rgb | null {
  const match = HEX.exec(text);
  if (!match) {
    return null;
  }
  let digits = match[1];
  if (digits.length <= 4) {
    digits = [...digits].map((digit) => digit + digit).join("");
  }
  const byte = (index: number) =>
    Number.parseInt(digits.slice(index, index + 2), 16);
  return {
    r: byte(0),
    g: byte(2),
    b: byte(4),
    a: digits.length === 8 ? byte(6) / 255 : 1,
  };
}

function fromRgbArgs(t: Token[], a: number): Rgb | null {
  const [r, g, b] = t.map((tok) => scaled(tok, 255));
  if (r === null || g === null || b === null) {
    return null;
  }
  return {
    r: clamp(r, 0, 255),
    g: clamp(g, 0, 255),
    b: clamp(b, 0, 255),
    a,
  };
}

function fromHueArgs(name: string, t: Token[], a: number): Rgb | null {
  const h = hue(t[0]);
  const second = percent(t[1]);
  const third = percent(t[2]);
  if (h === null || second === null || third === null) {
    return null;
  }
  if (name === "hwb") {
    return hwbToRgb({ h, w: second, b: third }, a);
  }
  if (name === "hsv") {
    return hsvToRgb({ h, s: second, v: third }, a);
  }
  return hslToRgb({ h, s: second, l: third }, a);
}

function fromCmykArgs(t: Token[], a: number): Rgb | null {
  const [c, m, y, k] = t.map(percent);
  if (c === null || m === null || y === null || k === null) {
    return null;
  }
  return cmykToRgb({ c, m, y, k }, a);
}

function fromFunction(name: string, body: string): Rgb | null {
  const [colorPart, slashAlpha] = body.split(SLASH);
  const parts = colorPart.split(SPLIT_ARGS).filter((part) => part !== "");
  const tokens: Token[] = [];
  for (const part of parts) {
    const tok = token(part);
    if (!tok) {
      return null;
    }
    tokens.push(tok);
  }
  const wanted = name === "cmyk" ? 4 : 3;
  let alphaToken =
    slashAlpha === undefined
      ? undefined
      : (token(slashAlpha.trim()) ?? undefined);
  if (slashAlpha !== undefined && !alphaToken) {
    return null;
  }
  if (tokens.length === wanted + 1 && slashAlpha === undefined) {
    alphaToken = tokens.pop();
  }
  if (tokens.length !== wanted) {
    return null;
  }
  const a = alphaOf(alphaToken);
  if (a === null) {
    return null;
  }
  if (name === "rgb" || name === "rgba") {
    return fromRgbArgs(tokens, a);
  }
  if (name === "cmyk") {
    return fromCmykArgs(tokens, a);
  }
  if (name === "hsl" || name === "hsla" || name === "hwb" || name === "hsv") {
    return fromHueArgs(name === "hsla" ? "hsl" : name, tokens, a);
  }
  return null;
}

export function parseColor(input: string): Rgb | null {
  const text = input.trim().toLowerCase();
  if (text === "") {
    return null;
  }
  if (text === "transparent") {
    return { r: 0, g: 0, b: 0, a: 0 };
  }
  const named = hexForName(text);
  if (named) {
    return parseHex(named);
  }
  const hex = parseHex(text);
  if (hex) {
    return hex;
  }
  const call = FUNCTION.exec(text);
  if (call) {
    return fromFunction(call[1], call[2]);
  }
  // A bare "255, 0, 128" or "255 0 128".
  return fromFunction("rgb", text);
}
